import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import mutationHooks from '../../common/mutationHooks/index.ts';
import { HydratedQuarantineFoundMeds } from '../../common/types/compoundModels.ts';

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  foundMeds?: HydratedQuarantineFoundMeds[] | null;
}

const QuarantineFoundMedsModal = ({ isOpen, setIsOpen, foundMeds }: IProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<M.Modal | null>(null);

  const { mutate: receiveMeds } =
    mutationHooks.quarantine.useUpdateReceiveManyById();

  const markAsReceivedAndClose = () => {
    const meds =
      foundMeds?.flatMap(fm => {
        if (fm.quarantineId == null || fm.epcisSgtinId == null) {
          return [];
        }

        return [
          {
            quarantineId: Number(fm.quarantineId),
            epcisSgtinId: Number(fm.epcisSgtinId),
          },
        ];
      }) ?? [];

    if (meds.length > 0) {
      receiveMeds({ meds });
    }

    instanceRef.current?.close();
  };

  useEffect(() => {
    if (!modalRef.current) return;

    instanceRef.current = M.Modal.init(modalRef.current, {
      onCloseEnd: () => setIsOpen(false),
      dismissible: false,
    });

    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [setIsOpen]);

  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance) return;

    if (isOpen) {
      instance.open();
    } else {
      instance.close();
    }
  }, [isOpen]);

  return (
    <div ref={modalRef} className="modal">
      <div className="modal-content">
        <div className="row">
          <h5 className="center">Med Not Found</h5>
          <div className="divider" />
        </div>

        <div
          className="row"
          style={{ textAlign: 'center', fontSize: '1.4rem' }}
        >
          The following items have been found to have EPCIS information in our
          system, and matched against existing document(s). Would you like to
          mark these meds as received? They will be marked as received on their
          given orders for the reported pharmacy, and they will be removed from
          quarantine<br></br>
          <br></br>
        </div>

        <table style={{ marginBottom: '20px' }}>
          <thead>
            <tr>
              <th>Pharmacy</th>
              <th>SerialNumber</th>
              <th>Med Name</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {foundMeds?.length &&
              foundMeds?.map((med, i) => {
                return (
                  <tr key={med.id}>
                    <td>{med.customerLocationName}</td>
                    <td>{med.serialNumber}</td>
                    <td>{med.regulatedProductName}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <div className="display-flex flex-column space-between"></div>

        <div className="row">
          <button
            className="btn-small green waves-effect waves-dark col s3 m4"
            onClick={markAsReceivedAndClose}
          >
            Quarantine Meds
          </button>
          <button
            className="btn-small red waves-effect waves-dark col s3 offset-m4 m4 "
            onClick={() => instanceRef.current?.close()}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuarantineFoundMedsModal;
